/* Angular Component */
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  ViewChild,
  OnDestroy,
  OnInit,
  EventEmitter,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MediaRepository } from "../../domain/media-repository";
import { MediaInfo, MediaLinkedType, MediaSegment } from "../../domain/media";
import libraryStore from "../../services/stores/library.store";
import { UserService } from "../../domain/user-service";
import { DownloadSelection, LibraryTypes } from "../../domain/enums";
import { UploadService } from "../../infrastructure/upload/upload-service";
import moment from "moment";
import { UI } from "../../services/ui.service";
import { ConversionUtils } from "../../domain/conversion-utils";
import { MediaService } from "../../services/media/media.service";
import { Subscription } from "rxjs";
import { Recording, RecordingStatus } from "../../domain/recording";
import { User } from "../../domain/user";
import { TenantSettingsRepository } from "../../domain/tenant-settings-repository";
import { TenantSettings } from "../../domain/tenant-settings";
import { HttpService } from "../../infrastructure/http/http";
import { RectangleFViewModel } from "../common/rectangle-f/rectangle-f.view-model";
import {
  MediaSegmentsViewModel,
  SegmentViewModel,
} from "./media-segments/media-segments.view-model";
import { ICommonUtilInterface } from "../../domain/common/common-util.interface";
import { S3CloudProvider } from "../../infrastructure/upload/s3-cloud-provider";
import { ICloudStorageProviderFactory } from "../../domain/Upload/i-cloud-storage-provider-factory";
import { Socket } from "../../infrastructure/socket.service";
import { EReplaceIdStrategy } from "../scheduler/library/library.component";
import { ToastrService } from "ngx-toastr";
import { OracleObjectStorageProvider } from "../../infrastructure/upload/oracle-object-storage-provider";
import { ChannelRepository } from "../../domain/channel-repository";

const DEFAULT_QCC_VIDEO_WIDTH = 500;
const DEFAULT_QCC_VIEW_ASPECT_RATION = 16 / 9;
const DEFAULT_QCC_VIDEO_HEIGHT =
  DEFAULT_QCC_VIDEO_WIDTH / DEFAULT_QCC_VIEW_ASPECT_RATION;

@Component({
  selector: "media-editor",
  templateUrl: "./mediaEditor.component.html",
  styleUrls: ["./mediaEditor.component.scss"],
})
export class MediaEditorComponent implements OnInit, OnDestroy {
  downloadSelection: DownloadSelection;
  tabs = [
    {
      title: "General",
      enabled: true,
    },
    {
      title: "META DATA",
      enabled: true,
    },
    {
      title: "Segments",
      enabled: false,
    },
    {
      title: "Files",
      enabled: true,
    },
    {
      title: "Position",
      enabled: true,
    },
  ];
  currentUser: User;
  uploadMediaToContract = false;
  routeSubscription;
  mediaId;
  media: MediaInfo;
  mediaSegmentsViewModel: MediaSegmentsViewModel;
  floatingMediaPositionVM: RectangleFViewModel;
  videoUrl: string;
  channelFramerate: number;
  libraryStore = libraryStore;
  selectedTabIndex = 0;
  qcPreviewSubtitleOffsetMs = 0;
  isSuperUser = false;
  newItem;
  showDownloadButton = true;
  showPreviewButton = true;
  recordingStatusChanged: Subscription;
  tenantSettings: TenantSettings;
  mediaQCWorkflowObjectivesChanged = false;
  markBoxStyle = {
    width: DEFAULT_QCC_VIDEO_WIDTH.toString() + "px",
    height: DEFAULT_QCC_VIDEO_HEIGHT.toString() + "px",
  };

  @ViewChild("contractDurationElement") contractDurationElement: ElementRef;
  seekPlayerToTime: EventEmitter<number> = new EventEmitter<number>();
  seekPlayerToTimeSubscription: Subscription;

  constructor(
    private ref: ElementRef,
    private route: ActivatedRoute,
    private zone: NgZone,
    private mediaRepository: MediaRepository,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private ui: UI,
    private uploadService: UploadService,
    private mediaService: MediaService,
    private _tenantSettingsRepository: TenantSettingsRepository,
    private _httpService: HttpService,
    private _commonUtils: ICommonUtilInterface,
    private _cloudStorageProviderFactory: ICloudStorageProviderFactory,
    private _socketService: Socket,
    private _toastr: ToastrService,
    private _channelRepository: ChannelRepository,
  ) {}

  onUploadFileChange(event) {
    for (let i = 0; i < event.target.files.length; i++) {
      this.uploadService.uploadMedia(event.target.files[i], this.media.id);
    }
    event.target.value = "";
    this.uploadMediaToContract = false;
  }
  /**
   * Initializing component
   * @return void
   */
  ngOnInit() {
    this._tenantSettingsRepository
      .getTenantSettings()
      .then((tenantSettings: TenantSettings) => {
        this.tenantSettings = tenantSettings;
        this.tabs.push({
          title: "License",
          enabled: this.tenantSettings.ShowMediaLicenseSettings,
        });
        this.tabs.push({
          title: "QC Status",
          enabled:
            this.tenantSettings.QCWorkflowObjectives.filter(
              (x) => x.isDeleted === false,
            ).length > 0,
        });
      });
    this.currentUser = this.userService.getCurrentUser();
    /* Assign Library Type */
    this.libraryStore.setLibraryType(LibraryTypes.Video);
    this.routeSubscription = this.route.params.subscribe((params) => {
      this.mediaId = +params["mediaId"];
      this.changeDetectorRef.markForCheck();
      this.changeDetectorRef.detectChanges();
      this.loadMediaInfo();
      this.videoUrl = null;

      this.getPreviewUrl();

      this.route.queryParams.subscribe((queryParams) => {
        /* Assign the selected tab index*/
        const selectedTabParam = queryParams["tabIndex"];
        if (selectedTabParam) {
          // Validate that selectedTabParam in scope of allowed tab indexes
          const parsedTabIndex = Number(selectedTabParam);
          if (parsedTabIndex >= 0 && parsedTabIndex <= this.tabs.length) {
            this.selectedTabIndex = parsedTabIndex;
          }
        }
      });

      this.isSuperUser = this.userService.getCurrentUser().isSuperUser;
    });

    this.userService.getCurrentChannel().then((currentChannel) => {
      this.channelFramerate = currentChannel ? currentChannel.framerate : null;
    });
    this.recordingStatusChanged = this.mediaService
      .getRecordingStatusChangedObservable()
      .subscribe(this.handleRecordStatusChanged.bind(this));
  }

  /**
   * Loading Media Info From Server
   * @return void
   */
  loadMediaInfo() {
    this.mediaRepository
      .getMediaInfo(this.mediaId)
      .then(
        this.onReceiveMediaInfo.bind(this),
        this.onReceiveMediaFailed.bind(this),
      )
      .catch(this.onReceiveMediaFailed.bind(this));
  }

  handleRecordStatusChanged(record: Recording) {
    if (record.associatedMediaId !== this.mediaId) {
      return;
    }
    this.media.duringRecording = record.status === RecordingStatus.Recording;
  }

  onContractDurationEnterClicked(event) {
    if (event.keyCode !== 13) {
      return;
    }
    this.editContractDuration(event);
    this.contractDurationElement.nativeElement.setAttribute(
      "contenteditable",
      false,
    );
    return false;
  }

  editContractDuration(event) {
    const newTime = this.checkAndSetNewTimeFormat(event.target.innerText);
    if (!newTime) {
      const message = !this.channelFramerate
        ? "You Must Provide Duration Formatted As Follows HH:mm:ss.SSS. (milliseconds)"
        : "You Must Provide Duration Formatted As Follows HH:mm:ff. (frames)";
      this.ui.notify(this.ui.EVENTS.ShowConfirm, {
        title: "Update Contract Duration",
        message: message,
      });
      this.contractDurationElement.nativeElement.innerText =
        this.formatContractDurationAndFps(this.media.contentDuration);
      return;
    } else {
      this.media.contentDuration = newTime;
    }
  }

  formatContractDurationAndFps(contractDuration: moment.Duration) {
    let formattedDuration = "";
    if (!this.channelFramerate) {
      formattedDuration = moment
        .utc(contractDuration.asMilliseconds())
        .format("HH:mm:ss.SSS");
    } else {
      const fps = ConversionUtils.getFpsFromMs(
        contractDuration.milliseconds(),
        this.channelFramerate,
      );
      formattedDuration =
        moment
          .utc(
            contractDuration.asMilliseconds() - contractDuration.milliseconds(),
          )
          .format("HH:mm:ss.") + (fps < 10 ? "0" + fps : "" + fps);
    }
    return formattedDuration;
  }

  checkAndSetNewTimeFormat(time: string) {
    const patternMs =
      /^(0*[0-9]|0*[1][0-9]|0*[2][0-3])\D{1}(0*[0-9]|0*[1-5][0-9])\D{1}(0*[0-9]|0*[1-5][0-9])\D{1}([0-9]{1}|[0-9]{2}|[0-9]{3})$/;
    const patternFs =
      /^(0*[0-9]|0*[1][0-9]|0*[2][0-3])\D{1}(0*[0-9]|0*[1-5][0-9])\D{1}(0*[0-9]|0*[1-5][0-9])\D{1}([0-9]{1}|[0-9]{2})$/;

    const match = time.match(this.channelFramerate ? patternFs : patternMs);
    if (!match) {
      return null;
    }

    // tslint:disable-next-line:radix
    return moment.duration(
      parseInt(match[1]) * 60 * 60 * 1000 +
        parseInt(match[2]) * 60 * 1000 +
        parseInt(match[3]) * 1000 +
        parseInt(match[4]),
      "ms",
    );
  }

  onContractDurationClick(event) {
    event.stopPropagation();
    this.contractDurationElement.nativeElement.setAttribute(
      "contenteditable",
      true,
    );
    this.contractDurationElement.nativeElement.focus();
  }

  getPreviewUrl() {
    if (!!this.media && this.media.mediaLinkedType !== 3) {
      this.mediaRepository.getMediaPreviewUrl(this.mediaId).then((result) => {
        this.videoUrl = this._changePreviewUrlPrefix(result);
        this.changeDetectorRef.markForCheck();
      });
    } else {
      this.videoUrl = "";
    }
  }

  onReceiveMediaInfo(response) {
    this.media = response;
    this.floatingMediaPositionVM = RectangleFViewModel.fromRectangleFObject(
      this.media.floatingMediaPosition,
    );
    this.floatingMediaPositionVM.updated.subscribe((newVal) => {
      this.media.floatingMediaPosition = newVal;
    });
    if (this.tenantSettings.UseMediaStartTimestampInScheduler) {
      this.qcPreviewSubtitleOffsetMs = this.media.startTimeStampMs;
    }

    const segmentsTab = this.tabs.find(
      (t) => t.title.toLowerCase().trim() === "segments",
    );
    segmentsTab.enabled = this.shouldSegmentsTabBeVisible();
    this.showDownloadButton =
      this.checkIfHlsFormat(this.media.transcodedUrl) === false;

    this.getPreviewUrl();
    this._initSegmentsViewModel();
    this.changeDetectorRef.markForCheck();
  }
  shouldSegmentsTabBeVisible() {
    return (
      this.media &&
      this.media.contentDuration.asMilliseconds() > 0 &&
      this.media.transcodedUrl &&
      this.media.transcodedUrl.length > 0 &&
      this.media.mediaLinkedType !== MediaLinkedType.Contract
    );
  }
  checkIfHlsFormat(videoUrl: string): boolean {
    return (
      videoUrl && videoUrl.length > 0 && videoUrl && videoUrl.includes(".m3u8")
    );
  }

  onReceiveMediaFailed(error) {
    console.log("Error", error);
  }

  /**
   *  Unsubscribing the route params change
   *  @return void
   */
  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.recordingStatusChanged) {
      this.recordingStatusChanged.unsubscribe();
    }
  }

  formatDurationAndFps() {
    if (!this.media) {
      return "";
    }

    const formattedDuration = this.media.formatDuration(this.channelFramerate);
    let formattedFps: number | string = "undefined";

    if (Number.isNaN(this.media.contentFps) === false) {
      formattedFps = Number.isInteger(this.media.contentFps)
        ? this.media.contentFps
        : (this.media.contentFps?.toFixed(3) ?? "undefined");
    }

    return formattedDuration + " (" + formattedFps.toString() + ")";
  }

  calculateAspectRatio() {
    if (!this.media || !this.media.contentWidth || !this.media.contentHeight) {
      return "";
    }

    const gcd = this.media.gcd();
    // console.log('calculateAspectRatio -> ',gcd);
    return (
      (
        (this.media.contentWidth * this.media.pixelAspectWidth) /
        gcd
      ).toString() +
      ":" +
      (
        (this.media.contentHeight * this.media.pixelAspectHeight) /
        gcd
      ).toString()
    );
  }

  openDropdown(dropDownElement: HTMLDivElement) {
    dropDownElement.classList.toggle("show");
  }

  getFileType(media: MediaInfo) {
    const index = media.userFilename.lastIndexOf(".");
    return index > -1 ? media.userFilename.slice(index + 1).toUpperCase() : "";
  }

  async DownloadFileChosen(val) {
    this.downloadSelection = val;
    switch (this.downloadSelection) {
      case DownloadSelection.OriginalFile:
        return this.mediaRepository.downloadOriginalFile(this.mediaId);
      case DownloadSelection.Transcoded:
        return this.mediaRepository.downloadTranscodedFile(this.mediaId);
      case DownloadSelection.Preview:
        return this.mediaRepository.downloadPreview(this.mediaId);
      case DownloadSelection.PlayTranscodedGra:
        this.videoUrl = null;
        this.changeDetectorRef.markForCheck();
        this.videoUrl = this.media.transcodedUrl;
        /*if (this.media.recording) {
                this.videoUrl = await this.signUrlOnOVHGra(this.media.transcodedUrl);
              }*/
        this.videoUrl = await this.signUrlOnOVHGra(this.media.transcodedUrl);
        this.changeDetectorRef.markForCheck();
        console.log(
          "media-editor->DownloadFileChosen->DownloadSelection.PlayTranscodedGra",
          this.videoUrl,
        );
        break;
      case DownloadSelection.PlayTranscodedWasabi:
        this.videoUrl = null;
        this.changeDetectorRef.markForCheck();
        const wasabiCredentials: any =
          await this._httpService.get("s3/credentials");
        //this.videoUrl = this.media.transcodedUrl;
        //if (this.media.recording) {
        this.videoUrl = await this.signUrlOnWasabi(
          this.media.transcodedUrl,
          wasabiCredentials.ProtectedBucketName,
          wasabiCredentials.RegionEndpoint,
        );
        //}
        this.changeDetectorRef.markForCheck();
        console.log(
          "media-editor->DownloadFileChosen->DownloadSelection.PlayTranscodedWasabi",
          this.videoUrl,
        );
        break;
      case DownloadSelection.PlayPreview:
        this.videoUrl = null;
        this.changeDetectorRef.markForCheck();
        this.getPreviewUrl();
        break;

      case DownloadSelection.PlayTranscodedWasabiLondon:
        this.videoUrl = null;
        this.changeDetectorRef.markForCheck();
        const s3Provider =
          this._cloudStorageProviderFactory.getCurrent() as S3CloudProvider;
        this.videoUrl = await this.signUrlOnWasabi(
          this.media.transcodedUrl,
          s3Provider.alternateBucketName,
          s3Provider.alternateRegionName,
        );

        this.changeDetectorRef.markForCheck();
        console.log(
          "media-editor->DownloadFileChosen->DownloadSelection.PlayTranscodedWasabi",
          this.videoUrl,
        );
        break;
      case DownloadSelection.PlayTranscodedOracle:
        this.videoUrl = null;
        this.changeDetectorRef.markForCheck();
        this.videoUrl = await this.mediaRepository.getMediaTranscodedUrl(
          this.mediaId,
        );
        this.changeDetectorRef.markForCheck();
        break;

      case DownloadSelection.PlayTranscodedAlternate:
        if (
          this.media.transcodedAlternateUrls &&
          this.media.transcodedAlternateUrls.length > 0
        ) {
          this.videoUrl = null;
          this.changeDetectorRef.markForCheck();
          this.videoUrl = this.media.transcodedAlternateUrls[0];
          this.changeDetectorRef.markForCheck();
        }

        break;
    }
  }

  private _changePreviewUrlPrefix(previewUrl: string): string {
    if (
      previewUrl &&
      previewUrl.length > 0 &&
      this.currentUser &&
      this.currentUser.previewUrlPrefix &&
      this.currentUser.previewUrlPrefix.length > 0
    ) {
      const parsedPreviewUrl = new URL(previewUrl);

      return (
        this.currentUser.previewUrlPrefix +
        parsedPreviewUrl.pathname +
        parsedPreviewUrl.search
      );
    } else {
      return previewUrl;
    }
  }

  calculateQccVideoSize() {
    const controlsPanelHeight = 30;
    return {
      width: DEFAULT_QCC_VIDEO_WIDTH.toString() + "px",
      height:
        (DEFAULT_QCC_VIDEO_HEIGHT + controlsPanelHeight).toString() + "px",
    };
  }

  getQccVideoSize() {
    return {
      width: DEFAULT_QCC_VIDEO_WIDTH,
      height: DEFAULT_QCC_VIDEO_HEIGHT,
    };
  }

  private async signUrlOnWasabi(
    url: string,
    overrideBucketName?: string,
    overrideRegionName?: string,
  ): Promise<string> {
    let endpointUrl = `S3/SignUrl?stringToSign=` + encodeURI(url);
    if (overrideBucketName && overrideRegionName) {
      endpointUrl += `&overrideBucketName=${encodeURI(overrideBucketName)}&overrideRegionName=${encodeURI(overrideRegionName)}`;
    }
    const result: string = await this._httpService.get(endpointUrl);
    return result;
  }

  private async signUrlOnOVHGra(url: string): Promise<string> {
    const result: string = await this._httpService.post(`Ovh/SignUrl`, {
      method: "GET",
      urlToSign: encodeURI(url),
    });
    return result;
  }

  private _initSegmentsViewModel() {
    this.mediaSegmentsViewModel = new MediaSegmentsViewModel(
      this.media,
      this._commonUtils,
    );
    this.mediaSegmentsViewModel.timeCodeType = this.channelFramerate
      ? "FPS"
      : "MS";
    this.seekPlayerToTimeSubscription =
      this.mediaSegmentsViewModel.seekPlayerToTime.subscribe((timeMs) => {
        this.seekPlayerToTime.emit(timeMs);
      });
  }

  public retrancodeCurrentMedia(): void {
    if (
      window.confirm(
        `Are you sure to retranscode the current media ${this.media.name}?`,
      )
    ) {
      this.mediaRepository
        .contentUpload(
          this.media.contentUrl,
          this.media.tenantMediaId,
          this._socketService.connectionId,
          null,
          null,
          null,
          { DuplicateIdHandlingStrategy: EReplaceIdStrategy.ReplaceFiles },
        )
        .then((response) => {
          //console.log('mediaEditor->retrancodeCurrentMedia-> this.mediaRepository.contentUpload', response);
          this._toastr.success(
            `Retranscoding process was scheduled for media ${this.media.id} ${this.media.name}`,
          );
        });
    }
  }

  async onTabsOutput(event: any) {
    if (event.key === "addQCWorkflowObjective") {
      const qcWorkflowObjectives =
        await this.mediaRepository.addQCWorkflowObjective(this.media.id);
      console.log(
        "mediaEditor->onTabsOutput->qcWorkflowObjectives",
        qcWorkflowObjectives,
      );
      this.media.mediaQCWorkflowObjectives = qcWorkflowObjectives;
      this.mediaQCWorkflowObjectivesChanged =
        !this.mediaQCWorkflowObjectivesChanged;
      this.changeDetectorRef.detectChanges();
    }
  }
}
